import { useState } from 'preact/hooks';
import { api } from '../api';
import { navigate } from '../router';
import { endSession, user } from '../state/session';

/**
 * Account deletion.
 *
 * Deliberately awkward: the button stays disabled until the account's own email is typed back.
 * This is the one action in the app that cannot be undone, and Play requires it to be plainly
 * reachable rather than buried behind a support request.
 */
export function DeleteAccount() {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const me = user.value;
  if (!me) return null;

  // Accounts created by magic link always have an email; fall back to a fixed word if one is
  // ever missing, so the confirmation can never be satisfied by an empty box.
  const phrase = me.email ?? 'DELETE';
  const matches = typed.trim().toLowerCase() === phrase.toLowerCase();

  const confirm = async () => {
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount();
      await endSession();
      navigate('/', { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <section class="section">
      <h2 class="section__title">Delete account</h2>

      {!confirming ? (
        <>
          <p class="muted">
            Permanently deletes your account, subscriptions, queue, playlists and listening
            history. This cannot be undone.
          </p>
          <button type="button" class="button button--ghost" onClick={() => setConfirming(true)}>
            Delete my account
          </button>
        </>
      ) : (
        <>
          <p class="muted">
            Type <strong>{phrase}</strong> to confirm. Everything below goes with it:
          </p>
          <ul class="muted">
            <li>Your sign-in and any passkeys</li>
            <li>Every subscription and playlist</li>
            <li>Your queue and listening positions</li>
          </ul>

          <label class="field">
            <span class="visually-hidden">Type {phrase} to confirm</span>
            <input
              type="text"
              value={typed}
              autocomplete="off"
              autocapitalize="none"
              spellcheck={false}
              disabled={busy}
              placeholder={phrase}
              onInput={(e) => setTyped((e.target as HTMLInputElement).value)}
            />
          </label>

          {error && <p class="error">{error}</p>}

          <div class="stack stack--row">
            <button
              type="button"
              class="button button--danger"
              onClick={() => void confirm()}
              disabled={!matches || busy}
            >
              {busy ? 'Deleting…' : 'Delete permanently'}
            </button>
            <button
              type="button"
              class="button button--ghost"
              onClick={() => {
                setConfirming(false);
                setTyped('');
                setError(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </section>
  );
}
